import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { combineLatest, firstValueFrom } from 'rxjs';
import { PaisDto } from '../../../../dto/expediente/pais.dto';
import { TipoDocumentoDto } from '../../../../dto/expediente/tipo-documento.dto';
import { ListaValorResponseDto } from '../../../../dto/tramites/lista_valor.dto';
import { Constants, FILTRO_TIPO_DOCUMENTO_INSOLVENCIA } from '../../../../constants/constants';
import { AlertaModalService } from '../../../../services/util/alerta-modal.service';
import { InfoPantallaDTO } from '../../../../dto/util/infoPantallaDTO';
import { TranslateService } from '@ngx-translate/core';
import { ListasService } from '../../../../services/listas.service';
import { TramiteService } from '../../../../services/tramite.service';
import { UtilService } from '../../../../services/util.service';
import { PersonaLoginDTO } from '../../../../dto/util/persona.dto';
import { FormField } from '../../../../dto/util/form-field';
import { TramiteValidacionJudicialDto } from '../../../../dto/tramites/tramite-validacion-judicial.dto';
import { Location } from '@angular/common';
import { TramiteSupersociedadesValidacionJudicialService } from '../../../../services/tramite-supersociedades-validacion-judicial.service';

@Component({
  selector: 'app-validacion-judicial',
  templateUrl: './validacion-judicial.component.html',
  styleUrl: './validacion-judicial.component.scss'
})
export class ValidacionJudicialComponent implements OnInit {
  paises: PaisDto[] = [];
  dataUsuario!: PersonaLoginDTO;
  listaTiposDocumento: TipoDocumentoDto[] = [];

  // Formularios
  tipoSolicitante: FormField[] = [];
  formTipoSolicitante!: FormGroup;

  // Multilenguaje
  vjiText: any = {};
  comunText: any = {};
  alertaText: any = {};

  tramiteVji?: TramiteValidacionJudicialDto;

  // Información de la pantalla y guía interactiva
  infoPantalla!: InfoPantallaDTO;

  listaSolicitante: ListaValorResponseDto[] = [];
  tramiteVjiVolver?: TramiteValidacionJudicialDto;
  nombreCompleto = '';

  constructor(
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    private readonly listaServices: ListasService,
    private readonly tramiteService: TramiteService,
    private readonly alertaModalService: AlertaModalService,
    private readonly location: Location,
    private readonly utilService: UtilService,
    private readonly serviceVji: TramiteSupersociedadesValidacionJudicialService
  ) {
    this.translate.stream('alertas').forEach(text => {
      for (const key of Object.keys(text)) {
        this.alertaText[key] = text[key];
      }
    });
  }

  ngOnInit(): void {
    this.nombreCompleto = this.getStorageItem(Constants.NOMBRE_TRAMITE) || '';
    combineLatest([
      this.translate.stream('tramites'),
      this.translate.stream('comun-label'),
    ]).subscribe(([tramitesText, comunLabelText]) => {
      // Procesar el texto de 'tramites'
      for (const key of Object.keys(tramitesText)) {
        this.vjiText[key] = tramitesText[key];
      }

      // Procesar el texto de 'comun-label'
      for (const key of Object.keys(comunLabelText)) {
        this.comunText[key] = comunLabelText[key];
      }
      this.initSolicitante();
    });
    this.dataUsuario = JSON.parse(
      sessionStorage.getItem(Constants.USER_INFORMATION_STORAGE)!
    );
    this.getListas();

    this.formTipoSolicitante = this.fb.group({});
    // Se valida si existe un trámite pendiente por completar
    this.tramiteService
      .consultarTramitePendiente(
        this.dataUsuario.idUsuario!,
        Constants.SSO_VJI
      )
      .subscribe(resp => {
        if ((resp as { Id: number }).Id !== null) {
          // Se consume el servicio que obtiene el detalle del trámite
          this.alertaModalService.confirmacion(
            this.alertaText['continuar-solicitud-almacenada'],
            '',
            () => {
              this.consultarTramitePorID(resp.Id, false);
            },
            () => {
              this.tramiteVji = undefined;
            },
            true
          );
        } else {
          this.tramiteVji = undefined;
        }
      });

    // Se asigna información a InfoPantallaDTO
    this.infoPantalla = {
      tituloPantalla: '',
      descripcionPantalla: '',
      pasos: [],
      // Evento en el botón volver
      change: async op =>
        await this.guardarAntesDeSalir(
          this.alertaModalService,
          this.location,
          op
        ),
    };
    this.utilService.changeInfoPantalla(this.infoPantalla);
  }

  // Se consume el servicio de consultar el detalle del trámite según el ID
  async consultarTramitePorID(id: number, isContinuar: boolean) {
    this.tramiteService
      .cargarTramitePendiente(id)
      .subscribe((resp: any) => {
        if (resp) {
          this.tramiteVji = resp;
          this.tipoSolicitante = this.tipoSolicitante.map(field => {
            if (field.name === 'tipoSolicitante') {
              field.value = this.getCodigoSolicitante(resp.tipoSolicitante.id);
            }
            return field;
          });
        }
      });
  }

  async guardarAntesDeSalir(alertas: AlertaModalService, loc: Location, op?: () => void) {
    await alertas.confirmacion(
      this.alertaText['recordatorio-campos-obligatorios'],
      this.alertaText['almacenar-progeso-solicitud'],
      async () => {
        try {
          this.tramiteVji = this.tramiteVjiVolver;
          if (this.tramiteVji) {
            this.tramiteVji.enviar = false;
            const response = await this.callServiceCrearTramite(this.tramiteVji);
            if (response.success) {
              if (op) {
                op();
              } else {
                loc.back();
              }
            } else {
              await alertas.confirmacion(
                this.alertaText['salir-o-continuar-diligenciando'],
                this.alertaText['datos-no-guardados'],
                () => {
                  if (op) {
                    op();
                  } else {
                    loc.back();
                  }
                },
                () => {
                  // El usuario decide continuar diligenciando
                }
              );
            }
          } else {
            await alertas.confirmacion(
              this.alertaText['salir-o-continuar-diligenciando'],
              this.alertaText['datos-no-guardados'],
              () => {
                if (op) {
                  op();
                } else {
                  loc.back();
                }
              },
              () => {
                // El usuario decide continuar diligenciando
              }
            );
          }
        } catch (error) {
          // Manejo de errores de llamada al servicio
          await alertas.confirmacion(
            this.alertaText['salir-o-continuar-diligenciando'],
            this.alertaText['datos-no-guardados'],
            () => {
              if (op) {
                op();
              } else {
                loc.back();
              }
            },
            () => {
              // El usuario decide continuar diligenciando
            }
          );
        }
      },
      () => {
        if (op) {
          op();
        } else {
          loc.back();
        }
      }
    );
  }

  getListas() {
    // Tipo solicitante
    this.listaServices
      .listaValor(Constants.ROI_TIPO_SOLICITANTE)
      .subscribe(response => {
        response = response.filter((elemento) => !['PNC', 'PNNC'].includes(elemento.Codigo!));
        this.listaSolicitante = response as ListaValorResponseDto[];
        const data = response as ListaValorResponseDto[];
        this.tipoSolicitante = this.tipoSolicitante.map(field => {
          if (field.name === 'tipoSolicitante') {
            field.options = data.map((item: ListaValorResponseDto) => ({
              Id: item.Codigo!,
              Nombre: item.Valor!,
            }));
          }
          return field;
        });
      });
    // Servicio que llama tipos de documento
    this.listaServices
      .getDocumentTypeList()
      .subscribe((documentTypes: TipoDocumentoDto[]) => {

        const mapLista = documentTypes.map(documentType => ({
          Id: documentType.Id,
          TipoDocumento: documentType.Tipo_Documento,
          Nombre: documentType.Descripcion
        }));

        const tiposPermitidos = FILTRO_TIPO_DOCUMENTO_INSOLVENCIA.PERSONA_TIPO_NATURAL;

        const filteredDocumentTypes = mapLista.filter(
          d => tiposPermitidos.includes(d.TipoDocumento ?? '')
        );

        this.listaTiposDocumento = filteredDocumentTypes;
      });
    // Servicio que llama al servicio de paises
    this.listaServices.getCountryList().subscribe((countries: PaisDto[]) => {
      this.paises = countries;
    });
  }

  // Init forms
  initSolicitante() {
    this.tipoSolicitante = [
      {
        name: 'tipoSolicitante',
        label: this.vjiText['reorganizacion-ordinaria-insolvencia']['tipo_solicitante'],
        type: 'select',
        required: true,
        visible: true,
        width: '6',
        options: [],
        errorMessages: {
          required: this.vjiText['reorganizacion-ordinaria-insolvencia']['campo-required'],
        },
        value: 'SOCIEDAD',
        disabled: true
      },
    ];
  }

  onSubmitTipoSolicitante(event: {
    controlName?: string;
    controlValue: unknown;
    form: FormGroup;
  }) {
    this.formTipoSolicitante = event.form;
    // if (event.controlName === 'tipoSolicitante') {
    //   if (this.tramiteVji != undefined) {
    //     if (this.tramiteVji.tipoSolicitante.id != this.formTipoSolicitante.get('tipoSolicitante')?.value) {
    //       this.alertaModalService.confirmacion(
    //         this.alertaText['roi']['tipo_solicitante'],
    //         this.alertaText['roi']['cambio_tipo_solicitante'],
    //         () => {
    //           this.formTipoSolicitante = event.form;
    //         }, () => {
    //           this.formTipoSolicitante.get('tipoSolicitante')?.setValue(this.tramiteVji?.tipoSolicitante.id)
    //         })
    //     }
    //   } else {
    //     this.formTipoSolicitante = event.form;
    //   }
    // }
  }

  async callServiceCrearTramite(
    tramite: TramiteValidacionJudicialDto
  ): Promise<{
    success: boolean;
    message?: string;
  }> {
    try {
      const response = await firstValueFrom(
        this.serviceVji.crearTramiteSSVjiSociedad(tramite)
      );
      if (response.success) {
        await this.alertaModalService.exito(
          this.alertaText['solicitud-pendiente-finalizar'],
          this.alertaText['roi']['tramite-enviado-mensaje']
        );
        return { success: true };
      } else {
        return { success: false };
      }
    } catch (error) {
      return { success: false };
    }
  }

  getIdSolicitante(codigo: string): number {
    let codigoLista: number = 0;
    this.listaSolicitante.forEach(elemento => {
      if (elemento.Codigo == codigo) {
        codigoLista = +elemento.Id!;
      }
    })
    return codigoLista;
  }

  getCodigoSolicitante(codigo: number): string {
    let codigoLista: string = '';
    this.listaSolicitante.forEach(elemento => {
      if (elemento.Id == codigo) {
        codigoLista = elemento.Codigo!;
      }
    })
    return codigoLista;
  }

  // Obtener body solicitudes
  getFormJuridica(tramite: TramiteValidacionJudicialDto) {
    this.tramiteVjiVolver = tramite;
  }

  getStorageItem(key: string): string | null {
    return sessionStorage.getItem(key) || localStorage.getItem(key);
  }
}
